import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { ArrowDownward } from '@mui/icons-material';
import {
  Box,
  SortDirection,
  SvgIconProps,
  TableCell as MuiTableCell,
  TableCellProps as MuiTableCellProps,
  TableSortLabel,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

const SortOptions: ReadonlyArray<SortDirection> = ['asc', 'desc', false];

const TableSortIconComponent = (props: SvgIconProps) => (
  <ArrowDownward data-testid="sorting-indicator" {...props} />
);

interface PlootoTableHeaderCellProps {
  sortDirection?: SortDirection;
  isSortable?: boolean;
  sortOrder?: number;
  onSortDirectionChange?: (newSortDirection: SortDirection) => void;
}

type TableHeaderCellProps = PlootoTableHeaderCellProps & MuiTableCellProps;

/**
 * Plooto Table Header Cell
 * @param props Table header cell props
 * @returns Function component
 */
const TableHeaderCell = (
  {
    sortDirection,
    isSortable,
    sortOrder,
    onSortDirectionChange,
    children,
    ...tableCellProps
  }: TableHeaderCellProps = {
    sortDirection: false,
    isSortable: false,
  }
) => {
  const handleSortClick = useCallback(() => {
    const newSortOptionIndex =
      (SortOptions.findIndex((option) => option === sortDirection) + 1) % 3;
    onSortDirectionChange?.(SortOptions[newSortOptionIndex]);
  }, [onSortDirectionChange, sortDirection]);

  const sortLabel = (
    <TableSortLabel
      active={!!sortDirection}
      direction={sortDirection || undefined}
      data-testid="sorting-icons"
      onClick={handleSortClick}
      IconComponent={TableSortIconComponent}
    >
      {children}
      {sortDirection ? (
        <Box component="span" sx={visuallyHidden}>
          {' '}
          {sortDirection === 'asc' ? (
            <FormattedMessage defaultMessage="sorted ascending" />
          ) : (
            <FormattedMessage defaultMessage="sorted descending" />
          )}
        </Box>
      ) : null}
    </TableSortLabel>
  );

  return (
    <MuiTableCell {...tableCellProps} sortDirection={isSortable ? sortDirection : false}>
      {isSortable ? sortLabel : children}
    </MuiTableCell>
  );
};

export type { TableHeaderCellProps };
export default TableHeaderCell;
